"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@xefi/x-react/button";
import { Modal } from "@xefi/x-react/modal";
import { addToast } from "@xefi/x-react/toast";
import { useTranslation } from "react-i18next";
import { MultiSelectAutoComplete } from "@/components/shared/Dashboard/MultiSelectAutoComplete";
import { useHasSofibImportToday } from "@/hooks/useHasSofibImportToday";
import { useUploadImport } from "@/hooks/useUploadImport";
import { useImportTrackerStore } from "@/store/useImportTrackerStore";
import type { ImportType } from "@/types/payments";
import { getApiErrorMessage } from "@/utils/apiError";
import ImportDropzone from "./ImportDropzone";
import { validateImportFile } from "./importValidation";
type Props = {
  isOpen: boolean;
  onClose: () => void;
};
const toImportType = (value: string | number | undefined): ImportType | null => {
  switch (value) {
    case "telecollections":
    case "missing_card_payments":
    case "national_transfers_sofib":
    case "national_transfers_512":
      return value;
    default:
      return null;
  }
};
export default function ImportModal(t0) {
  const $ = _c(98);
  const {
    isOpen,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const trackImport = useImportTrackerStore(_temp);
  const [type, setType] = useState(null);
  const [file, setFile] = useState(null);
  const {
    mutate: upload,
    isPending
  } = useUploadImport();
  const {
    data: t1
  } = useHasSofibImportToday(isOpen);
  const hasSofibToday = t1 === undefined ? false : t1;
  const isFiveTwelveLocked = !hasSofibToday;
  let t2;
  if ($[0] !== isFiveTwelveLocked) {
    t2 = isFiveTwelveLocked ? ["national_transfers_512"] : [];
    $[0] = isFiveTwelveLocked;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const disabledTypeIds = t2;
  let t3;
  if ($[2] !== t) {
    t3 = () => addToast({
      title: t("paymentsReceived.import.sofibRequiredTitle"),
      description: t("paymentsReceived.import.sofibRequiredHint"),
      color: "warning"
    });
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const notifyFiveTwelveLocked = t3;
  let t4;
  if ($[4] !== t) {
    t4 = t("paymentsReceived.tabs.telecollections");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = {
      id: "telecollections",
      name: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("paymentsReceived.tabs.missingCardPayments");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = {
      id: "missing_card_payments",
      name: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("paymentsReceived.imports.types.national_transfers_sofib");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = {
      id: "national_transfers_sofib",
      name: t8
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t) {
    t10 = t("paymentsReceived.imports.types.national_transfers_512");
    $[16] = t;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = {
      id: "national_transfers_512",
      name: t10
    };
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t5 || $[22] !== t7 || $[23] !== t9) {
    t12 = [t5, t7, t9, t11];
    $[20] = t11;
    $[21] = t5;
    $[22] = t7;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const typeOptions = t12;
  let t13;
  if ($[25] !== type || $[26] !== typeOptions) {
    t13 = typeOptions.filter(option => option.id === type);
    $[25] = type;
    $[26] = typeOptions;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const selectedType = t13;
  let t14;
  if ($[28] !== onClose) {
    t14 = () => {
      setType(null);
      setFile(null);
      onClose();
    };
    $[28] = onClose;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const resetAndClose = t14;
  let t15;
  if ($[30] !== t) {
    t15 = nextFile => {
      if (!nextFile) {
        setFile(null);
        return;
      }
      const errorKey = validateImportFile(nextFile);
      if (errorKey) {
        addToast({
          title: t(errorKey),
          color: "danger"
        });
        setFile(null);
        return;
      }
      setFile(nextFile);
    };
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const handleFileSelect = t15;
  let t16;
  if ($[32] !== file || $[33] !== isFiveTwelveLocked || $[34] !== notifyFiveTwelveLocked || $[35] !== queryClient || $[36] !== resetAndClose || $[37] !== t || $[38] !== trackImport || $[39] !== type || $[40] !== upload) {
    t16 = () => {
      if (!type) {
        addToast({
          title: t("paymentsReceived.import.selectTypeFirst"),
          color: "warning"
        });
        return;
      }
      if (!file) {
        addToast({
          title: t("paymentsReceived.import.selectFileFirst"),
          color: "warning"
        });
        return;
      }
      if (type === "national_transfers_512" && isFiveTwelveLocked) {
        notifyFiveTwelveLocked();
        return;
      }
      const pendingFile = file;
      const pendingType = type;
      addToast({
        title: t("paymentsReceived.import.started"),
        color: "success"
      });
      resetAndClose();
      upload({
        file: pendingFile,
        type: pendingType
      }, {
        onSuccess: created => {
          if (created?.id) {
            trackImport(created.id);
          } else {
            queryClient.invalidateQueries({
              queryKey: ["payments", "imports"]
            });
          }
        },
        onError: error => addToast({
          title: getApiErrorMessage(error, t("paymentsReceived.import.error")),
          color: "danger"
        })
      });
    };
    $[32] = file;
    $[33] = isFiveTwelveLocked;
    $[34] = notifyFiveTwelveLocked;
    $[35] = queryClient;
    $[36] = resetAndClose;
    $[37] = t;
    $[38] = trackImport;
    $[39] = type;
    $[40] = upload;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const handleSubmit = t16;
  let t17;
  if ($[42] !== resetAndClose) {
    t17 = open => {
      if (!open) {
        resetAndClose();
      }
    };
    $[42] = resetAndClose;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t) {
    t18 = t("paymentsReceived.import.title");
    $[44] = t;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t) {
    t19 = t("paymentsReceived.import.cancel");
    $[46] = t;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== isPending || $[49] !== resetAndClose || $[50] !== t19) {
    t20 = <Button variant="bordered" radius="sm" onClick={resetAndClose} isDisabled={isPending}>{t19}</Button>;
    $[48] = isPending;
    $[49] = resetAndClose;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t) {
    t21 = t("paymentsReceived.import.submit");
    $[52] = t;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== handleSubmit || $[55] !== isPending || $[56] !== t21) {
    t22 = <Button color="primary" radius="sm" onClick={handleSubmit} isLoading={isPending} isDisabled={isPending}>{t21}</Button>;
    $[54] = handleSubmit;
    $[55] = isPending;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t20 || $[59] !== t22) {
    t23 = <>{t20}{t22}</>;
    $[58] = t20;
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== t) {
    t24 = t("paymentsReceived.import.type");
    $[61] = t;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t24) {
    t25 = <p className="mb-1 text-sm">{t24}</p>;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t) {
    t26 = t("paymentsReceived.import.typePlaceholder");
    $[65] = t;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== isFiveTwelveLocked || $[68] !== notifyFiveTwelveLocked) {
    t27 = value => {
      const nextType = toImportType(value[0]?.id);
      if (nextType === "national_transfers_512" && isFiveTwelveLocked) {
        notifyFiveTwelveLocked();
        return;
      }
      setType(nextType);
    };
    $[67] = isFiveTwelveLocked;
    $[68] = notifyFiveTwelveLocked;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== disabledTypeIds || $[71] !== isPending || $[72] !== selectedType || $[73] !== t26 || $[74] !== t27 || $[75] !== typeOptions) {
    t28 = <MultiSelectAutoComplete name="import-type" placeholder={t26} options={typeOptions} selected={selectedType} onSelect={t27} selectionMode="single" className="w-full" isFetching={false} isDisabled={isPending} disabledOptionIds={disabledTypeIds} />;
    $[70] = disabledTypeIds;
    $[71] = isPending;
    $[72] = selectedType;
    $[73] = t26;
    $[74] = t27;
    $[75] = typeOptions;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== isFiveTwelveLocked || $[78] !== t) {
    t29 = isFiveTwelveLocked && <p className="mt-1 text-xs text-foreground/60">{t("paymentsReceived.import.sofibRequiredHint")}</p>;
    $[77] = isFiveTwelveLocked;
    $[78] = t;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] !== t25 || $[81] !== t28 || $[82] !== t29) {
    t30 = <div>{t25}{t28}{t29}</div>;
    $[80] = t25;
    $[81] = t28;
    $[82] = t29;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  let t31;
  if ($[84] !== file || $[85] !== handleFileSelect || $[86] !== isPending) {
    t31 = <ImportDropzone file={file} onFileSelect={handleFileSelect} disabled={isPending} />;
    $[84] = file;
    $[85] = handleFileSelect;
    $[86] = isPending;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  let t32;
  if ($[88] !== t30 || $[89] !== t31) {
    t32 = <div className="flex flex-col gap-4">{t30}{t31}</div>;
    $[88] = t30;
    $[89] = t31;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== isOpen || $[92] !== resetAndClose || $[93] !== t17 || $[94] !== t18 || $[95] !== t23 || $[96] !== t32) {
    t33 = <Modal isOpen={isOpen} onClose={resetAndClose} onOpenChange={t17} size="lg" title={t18} footer={t23}>{t32}</Modal>;
    $[91] = isOpen;
    $[92] = resetAndClose;
    $[93] = t17;
    $[94] = t18;
    $[95] = t23;
    $[96] = t32;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  return t33;
}
function _temp(state) {
  return state.track;
}